import IconGithub from "central-icons/IconGithub";
import { IconGitLab } from "@/assets/icons/geist/IconGitLab";
import { IconBitbucket } from "@/assets/icons/geist/IconBitbucket";
import type { BaseIconProps } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import {
    scmAuthStatusesAtom,
    getScmAuthStatus,
    lastSelectedScmAuthHostAtom,
    currentStepAtom,
    ONBOARDING_STEP_SLUG,
    ONBOARDING_STEP,
    type AsyncQuery,
} from "@/routes/ona-onboarding/onboarding-state";
import { OnboardingStepIndicator } from "@/routes/ona-onboarding/OnboardingStepIndicator";
import { StepHeadline } from "@/routes/ona-onboarding/StepHeadline";
import { StepSubheader } from "@/routes/ona-onboarding/StepSubheader";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useAtom, useAtomValue } from "jotai";
import { atomEffect } from "jotai-effect";
import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/components/podkit/lib/cn";
import SkipOnboardingButton from "@/routes/ona-onboarding/SkipOnboardingButton";
import { PageCategories, usePageTracking } from "@/hooks/use-page-tracking";
import type { AuthenticatedWithRunnerResponse } from "@/queries/runner-queries";

export function ConnectToScm() {
    usePageTracking("Connect to SCM", PageCategories.OnaOnboarding);
    const navigate = useNavigate();

    useAtom(
        useMemo(
            () =>
                atomEffect((get, set) => {
                    const scmAuthStatusMap = get(scmAuthStatusesAtom);
                    const scmAuthStatuses = Object.values(scmAuthStatusMap);
                    const currentStep = get(currentStepAtom);
                    const lastSelectedScmAuthHost = get(lastSelectedScmAuthHostAtom);

                    if (lastSelectedScmAuthHost) {
                        // the user has selected the scm host they want to connect with

                        // Check if ALL or last selected SCM is authenticated
                        const scmAuthSuccessful = (resp: AsyncQuery<AuthenticatedWithRunnerResponse>) =>
                            resp.type === "success" && resp.data.type === "Authenticated";
                        const allAuthSuccessful =
                            scmAuthStatuses.length > 0 && scmAuthStatuses.every(scmAuthSuccessful);
                        const lastSelectedScmHostAuthSuccessful = lastSelectedScmAuthHost
                            ? scmAuthSuccessful(scmAuthStatusMap[lastSelectedScmAuthHost])
                            : false;

                        if (
                            (allAuthSuccessful || lastSelectedScmHostAuthSuccessful) &&
                            currentStep.slug === ONBOARDING_STEP_SLUG.connectToScm
                        ) {
                            set(currentStepAtom, ONBOARDING_STEP[ONBOARDING_STEP_SLUG.selectRepository]);
                            navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.selectRepository}`);
                        }
                    }
                }),
            [navigate],
        ),
    );

    return (
        <div className="mx-0 mt-0 flex flex-col items-center justify-start p-8 md:m-auto md:justify-center">
            <OnboardingStepIndicator className="mb-8" />
            <StepHeadline className="mb-2">Connect to Git</StepHeadline>
            <StepSubheader className="mb-4">Select a repository for Ona</StepSubheader>
            <div className="mt-4 flex flex-col gap-3">
                <ConnectToGitHubButton />
                <ConnectToBitbucketButton />
                <GitProviderComingSoonButton provider="GitLab" Icon={IconGitLab} analyticsProvider="SCM - GitLab" />
                <SkipOnboardingButton trackingId="skip-onboarding-connect-to-scm" />
            </div>
        </div>
    );
}

function ConnectToGitHubButton() {
    const [lastSelectedScmAuthHost, setLastSelectedScmAuthHost] = useAtom(lastSelectedScmAuthHostAtom);
    const scmAuthStatuses = useAtomValue(scmAuthStatusesAtom);
    const githubAuthStatusQuery = getScmAuthStatus(scmAuthStatuses, "github.com");

    function beginGitHubAuth() {
        setLastSelectedScmAuthHost("github.com");
        if (githubAuthStatusQuery.type === "success" && githubAuthStatusQuery.data.type === "AuthenticationRequired") {
            window.open(githubAuthStatusQuery.data.url, "_blank");
        }
    }

    return (
        <Button
            loading={githubAuthStatusQuery.type === "pending" || lastSelectedScmAuthHost === "github.com"}
            disabled={lastSelectedScmAuthHost !== null}
            variant="primary"
            size="lg"
            className="h-10 w-56 px-8 py-2"
            LeadingIcon={() => <IconGithub className="mr-1 h-4 w-4" />}
            onClick={beginGitHubAuth}
            data-tracking-id="connect-to-github-onboarding-connect-to-scm-page"
        >
            Connect to GitHub
        </Button>
    );
}

function ConnectToBitbucketButton() {
    const [lastSelectedScmAuthHost, setLastSelectedScmAuthHost] = useAtom(lastSelectedScmAuthHostAtom);
    const scmAuthStatuses = useAtomValue(scmAuthStatusesAtom);
    const bitbucketAuthStatusQuery = getScmAuthStatus(scmAuthStatuses, "bitbucket.org");

    function beginBitbucketAuth() {
        setLastSelectedScmAuthHost("bitbucket.org");
        if (
            bitbucketAuthStatusQuery.type === "success" &&
            bitbucketAuthStatusQuery.data.type === "AuthenticationRequired"
        ) {
            window.open(bitbucketAuthStatusQuery.data.url, "_blank");
        }
    }

    return (
        <Button
            loading={bitbucketAuthStatusQuery.type === "pending" || lastSelectedScmAuthHost === "bitbucket.org"}
            disabled={lastSelectedScmAuthHost !== null}
            variant="primary"
            size="lg"
            className="h-10 px-8 py-2"
            LeadingIcon={() => <IconBitbucket className="mr-1 h-4 w-4" size="base" />}
            onClick={beginBitbucketAuth}
            data-tracking-id="connect-to-bitbucket-onboarding-connect-to-scm-page"
        >
            Connect to Bitbucket
        </Button>
    );
}

type GitProviderComingSoonButtonProps = {
    provider: string;
    Icon: React.ComponentType<BaseIconProps>;
    analyticsProvider: string;
};

function GitProviderComingSoonButton({ provider, Icon, analyticsProvider }: GitProviderComingSoonButtonProps) {
    const segmentTrack = useSegmentTrack();
    const [waitListJoined, setWaitListJoined] = useState(false);

    const onJoinWaitlist = useCallback(() => {
        segmentTrack("waitlist_joined", {
            type: "git_provider",
            provider,
            feature: analyticsProvider,
        });
        setWaitListJoined(true);
    }, [segmentTrack, analyticsProvider, provider]);

    const handleClick = useCallback(() => {
        if (!waitListJoined) {
            onJoinWaitlist();
        }
    }, [onJoinWaitlist, waitListJoined]);

    const label = waitListJoined ? "Coming soon" : provider;

    return (
        <Button
            variant="secondary"
            size="lg"
            className={cn(
                "h-10 w-56 px-8 py-2",
                waitListJoined && "border-content-green bg-surface-02 text-content-green",
            )}
            LeadingIcon={() => <Icon className="mr-1 h-4 w-4" size="base" />}
            onClick={handleClick}
            disabled={waitListJoined}
            aria-label={label}
            data-tracking-id={`connect-to-${provider}-coming-soon-onboarding-connect-to-scm-page`}
        >
            {label}
        </Button>
    );
}
